import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export type PersonRow = {
  id: number;
  name: string;
  slug: string;
  chip_color: string | null;
  avatar_color: string | null;
  active: boolean;
};

export type VendorRow = {
  id: number;
  name: string;
  slug: string;
  chip_color: string | null;
  tile_color: string | null;
  active: boolean;
};

export function usePeople() {
  const api = useApi();
  return useQuery({
    queryKey: ["people"],
    queryFn: async (): Promise<PersonRow[]> => {
      const res = await api(`/api/people`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as PersonRow[];
    },
    staleTime: 60_000,
  });
}

export function useVendors() {
  const api = useApi();
  return useQuery({
    queryKey: ["vendors"],
    queryFn: async (): Promise<VendorRow[]> => {
      const res = await api(`/api/vendors`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as VendorRow[];
    },
    staleTime: 60_000,
  });
}

export function useCreatePerson() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<PersonRow> => {
      const res = await api(`/api/people`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return (await res.json()) as PersonRow;
    },
    onSuccess: (newPerson) => {
      qc.setQueryData<PersonRow[]>(["people"], (old) => {
        if (!old) return [newPerson];
        if (old.some((p) => p.id === newPerson.id)) return old;
        return [...old, newPerson].sort((a, b) => a.name.localeCompare(b.name));
      });
    },
  });
}

export function useCreateVendor() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<VendorRow> => {
      const res = await api(`/api/vendors`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return (await res.json()) as VendorRow;
    },
    onSuccess: (newVendor) => {
      qc.setQueryData<VendorRow[]>(["vendors"], (old) => {
        if (!old) return [newVendor];
        if (old.some((v) => v.id === newVendor.id)) return old;
        return [...old, newVendor].sort((a, b) => a.name.localeCompare(b.name));
      });
    },
  });
}
